import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { SelectOption } from "@/components/ui/select-dropdown";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";
import type { GuardiansFilterValues } from "./GuardiansFilterBar";
import { CompressibleToolbarLabel } from "./CompressibleToolbarLabel";
import { GUARDIANS_FILTER_CHIP_MIN_WIDTH_PX } from "./compressToolbarLabel";
import { GuardiansToolbarAnimatedSlot } from "./GuardiansToolbarAnimatedSlot";
import type { GuardiansView } from "../guardiansParams";
import {
  guardiansActiveFilterChips,
  guardiansFiltersToolbarVisible,
  type ActiveFilterChipId,
  type GuardiansTabFilterState,
} from "./guardiansViewFilters";

export interface GuardiansActiveFilterChipsProps {
  view: GuardiansView;
  values: GuardiansFilterValues;
  tabFilters: GuardiansTabFilterState;
  missionTemplates: SelectOption<string>[];
  agents: SelectOption<string>[];
  catalogues: SelectOption<string>[];
  scopeLabels: ScopeLabelCache;
  onRemove: (id: ActiveFilterChipId) => void;
}

function optionLabel(options: SelectOption<string>[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

export function GuardiansActiveFilterChips({
  view,
  values,
  tabFilters,
  missionTemplates,
  agents,
  catalogues,
  scopeLabels,
  onRemove,
}: GuardiansActiveFilterChipsProps) {
  const visible = guardiansFiltersToolbarVisible(view);
  const chips = guardiansActiveFilterChips(view, values, tabFilters, {
    taskLabel: (id) => scopeLabels.taskLabel(id),
    missionTemplateOptionLabel: (id) => optionLabel(missionTemplates, id),
    agentOptionLabel: (id) => optionLabel(agents, id),
    catalogueOptionLabel: (id) => optionLabel(catalogues, id),
  });

  return (
    <GuardiansToolbarAnimatedSlot visible={visible && chips.length > 0} asWorkbenchSlot={false}>
      <div
        className="flex min-w-0 flex-auto items-center justify-start gap-1 overflow-hidden"
        data-testid="guardians-active-filter-chips"
        data-visible={visible && chips.length > 0 ? "true" : "false"}
      >
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            title={chip.label}
            data-filter-chip
            data-filter-chip-id={chip.id}
            onClick={() => onRemove(chip.id)}
            className="group inline-flex min-w-0 max-w-full shrink items-center gap-1 overflow-hidden rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[11px] font-medium text-indigo-200 hover:bg-indigo-500/20"
            style={{ minWidth: GUARDIANS_FILTER_CHIP_MIN_WIDTH_PX }}
          >
            <CompressibleToolbarLabel label={chip.label} className="min-w-0" />
            <FontAwesomeIcon
              icon={faXmark}
              className="shrink-0 text-[10px] text-indigo-400 group-hover:text-indigo-100"
            />
          </button>
        ))}
      </div>
    </GuardiansToolbarAnimatedSlot>
  );
}
